import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Inline from "../components/layout/Inline";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import SelectInput from "../components/forms/SelectInput";
import { TableShell, Table, Th, Td } from "../components/data/Table";

const errorTextStyle = {
  color: "var(--color-danger)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const normalizeStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["pending", "approved", "suspended"].includes(normalized)) {
    return normalized;
  }
  return "pending";
};

const getStatusForExcursion = (member, excursionId) => {
  const normalizedExcursionId = String(excursionId || "");

  const matchingSubscription = Array.isArray(member?.subscriptions)
    ? member.subscriptions.find(
        (subscription) =>
          String(subscription?.excursion_id) === normalizedExcursionId
      )
    : null;

  if (matchingSubscription?.subscription_status) {
    return normalizeStatus(matchingSubscription.subscription_status);
  }

  if (member?.subscription_status) {
    return normalizeStatus(member.subscription_status);
  }

  return "pending";
};

const ManageMembers = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursionId, setSelectedExcursionId] = useState("");

  const [members, setMembers] = useState([]);
  const [subscriptionStatusMap, setSubscriptionStatusMap] = useState({});
  const [originalStatusMap, setOriginalStatusMap] = useState({});
  const [newMembers, setNewMembers] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const eligible = user?.role === "admin";

  useEffect(() => {
    const fetchExcursions = async () => {
      if (!eligible) {
        setLoadingPage(false);
        return;
      }

      try {
        setError(null);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          { headers }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const found = data.find(
            (ex) => String(ex.excursion_id) === activeId
          );

          if (found) {
            setSelectedExcursionId(activeId);
            return;
          }
        }

        if (data.length > 0) {
          setSelectedExcursionId(String(data[0].excursion_id));
        }
      } catch (err) {
        console.error(err);
        setError(t("manage_members.errors.load_excursions"));
      } finally {
        setLoadingPage(false);
      }
    };

    if (user && token) fetchExcursions();
  }, [user, eligible, headers, token, t]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!selectedExcursionId || !eligible) return;

      try {
        setError(null);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/${selectedExcursionId}/all-members`,
          { headers }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setMembers(data);

        const statusMap = {};
        data.forEach((member) => {
          statusMap[member.user_id] = getStatusForExcursion(
            member,
            selectedExcursionId
          );
        });

        setSubscriptionStatusMap(statusMap);
        setOriginalStatusMap({ ...statusMap });
      } catch (err) {
        console.error(err);
        setMembers([]);
        setSubscriptionStatusMap({});
        setOriginalStatusMap({});
        setError(t("manage_members.errors.load_members"));
      }
    };

    fetchMembers();
  }, [selectedExcursionId, eligible, headers, t]);

  const handleStatusChange = (userId, newStatus) => {
    setSubscriptionStatusMap((prev) => ({
      ...prev,
      [userId]: normalizeStatus(newStatus),
    }));
  };

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursionId)
  );

  const isExcursionLocked =
    selectedExcursionData?.status?.toLowerCase() !== "live";

  const handleAddNewRow = () => {
    if (isExcursionLocked) {
      setError(t("manage_members.errors.excursion_locked"));
      return;
    }

    setNewMembers((prev) => [
      ...prev,
      {
        name: "",
        surname: "",
        phone_number: "",
        subscription_status: "pending",
      },
    ]);
  };

  const handleUpdate = async () => {
    if (!eligible || !selectedExcursionId || isSaving) return;

    if (isExcursionLocked) {
      setError(t("manage_members.errors.excursion_locked"));
      return;
    }

    const updates = Object.entries(subscriptionStatusMap)
      .filter(([id, status]) => originalStatusMap[id] !== status)
      .map(([id, status]) => ({
        user_id: parseInt(id, 10),
        subscription_status: status,
      }));

    const inserts = newMembers
      .map((member) => ({
        ...member,
        subscription_status: normalizeStatus(member.subscription_status),
      }))
      .filter((member) => member.name && member.surname && member.phone_number);

    if (!updates.length && !inserts.length) {
      alert(t("manage_members.alerts.no_changes"));
      return;
    }

    try {
      setIsSaving(true);

      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/excursions/${selectedExcursionId}/delupsert-members`,
        { updates, inserts },
        { headers }
      );

      alert(res.data?.message || t("manage_members.alerts.success"));

      setNewMembers([]);

      const refreshed = await axios.get(
        `${process.env.REACT_APP_API_URL}/excursions/${selectedExcursionId}/all-members`,
        { headers }
      );

      const refreshedData = Array.isArray(refreshed.data) ? refreshed.data : [];
      setMembers(refreshedData);

      const refreshedStatusMap = {};
      refreshedData.forEach((member) => {
        refreshedStatusMap[member.user_id] = getStatusForExcursion(
          member,
          selectedExcursionId
        );
      });

      setSubscriptionStatusMap(refreshedStatusMap);
      setOriginalStatusMap({ ...refreshedStatusMap });
    } catch (err) {
      console.error(err);
      alert(t("manage_members.alerts.failed"));
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingUser || loadingPage) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_members.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_members.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>{t("manage_members.errors.no_user")}</div>
        }
      />
    );
  }

  if (!eligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("manage_members.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("manage_members.errors.no_access")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.short_name} (${selectedExcursionData.start_date} → ${selectedExcursionData.end_date})`
    : loadingPage
    ? t("manage_members.loading.excursions")
    : t("manage_members.no_selection");

  return (
    <StandardScreen
      topNav={<TopNav title={t("manage_members.title")} mode="main" />}
      bottomAction={
        <Inline gap="3">
          <Button
            onClick={handleUpdate}
            disabled={isSaving || isExcursionLocked}
          >
            {isSaving
              ? t("manage_members.actions.updating")
              : t("manage_members.actions.update")}
          </Button>

          <Button
            variant="secondary"
            onClick={handleAddNewRow}
            disabled={isExcursionLocked}
          >
            {t("manage_members.actions.add")}
          </Button>
        </Inline>
      }
    >
      <ScreenContent>
        <Stack gap="5">
          <Card title={t("manage_members.my_excursion")}>
            <FormField>
              <input
                type="text"
                value={selectedExcursionLabel}
                disabled
                readOnly
                style={disabledInputStyle}
              />
            </FormField>

            {isExcursionLocked && (
              <div style={errorTextStyle}>
                {t("manage_members.errors.excursion_locked")}
              </div>
            )}

            {error && <div style={errorTextStyle}>{error}</div>}
          </Card>

          <Card title={t("manage_members.members.title")}>
            <TableShell>
              <Table>
                <thead>
                  <tr>
                    <Th columnIndex={0} sticky>
                      {t("manage_members.members.columns.member")}
                    </Th>
                    <Th>{t("manage_members.members.columns.phone")}</Th>
                    <Th>{t("manage_members.members.columns.status")}</Th>
                  </tr>
                </thead>

                <tbody>
                  {members.map((member) => (
                    <tr key={member.user_id}>
                      <Td columnIndex={0} sticky>
                        {`${member.name} ${member.surname}`}
                      </Td>
                      <Td>{member.phone_number}</Td>
                      <Td>
                        <SelectInput
                          value={normalizeStatus(
                            subscriptionStatusMap[member.user_id]
                          )}
                          onChange={(e) =>
                            handleStatusChange(member.user_id, e.target.value)
                          }
                          disabled={isExcursionLocked}
                        >
                          {["pending", "approved", "suspended"].map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </SelectInput>
                      </Td>
                    </tr>
                  ))}

                  {newMembers.map((member, index) => (
                    <tr key={`new-${index}`}>
                      <Td columnIndex={0} sticky>
                        <Inline gap="2" wrap>
                          <TextInput
                            value={member.name}
                            disabled={isExcursionLocked}
                            onChange={(e) =>
                              setNewMembers((prev) =>
                                prev.map((row, i) =>
                                  i === index
                                    ? { ...row, name: e.target.value }
                                    : row
                                )
                              )
                            }
                          />
                          <TextInput
                            value={member.surname}
                            disabled={isExcursionLocked}
                            onChange={(e) =>
                              setNewMembers((prev) =>
                                prev.map((row, i) =>
                                  i === index
                                    ? { ...row, surname: e.target.value }
                                    : row
                                )
                              )
                            }
                          />
                        </Inline>
                      </Td>

                      <Td>
                        <TextInput
                          value={member.phone_number}
                          disabled={isExcursionLocked}
                          onChange={(e) =>
                            setNewMembers((prev) =>
                              prev.map((row, i) =>
                                i === index
                                  ? { ...row, phone_number: e.target.value }
                                  : row
                              )
                            )
                          }
                        />
                      </Td>

                      <Td>
                        <SelectInput
                          value={normalizeStatus(member.subscription_status)}
                          disabled={isExcursionLocked}
                          onChange={(e) =>
                            setNewMembers((prev) =>
                              prev.map((row, i) =>
                                i === index
                                  ? {
                                      ...row,
                                      subscription_status: normalizeStatus(
                                        e.target.value
                                      ),
                                    }
                                  : row
                              )
                            )
                          }
                        >
                          {["pending", "approved", "suspended"].map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </SelectInput>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableShell>
          </Card>
        </Stack>
      </ScreenContent>
    </StandardScreen>
  );
};

export default ManageMembers;